package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.specialdays.LocalEventDto
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.ListenerErrors
import com.evoapps.evoshift.domain.model.LocalEvent
import com.evoapps.evoshift.domain.repository.LocalEventRepository
import dev.gitlive.firebase.firestore.QuerySnapshot
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.LocalDate

/**
 * SH-227: `workplaces/{id}/local_events/{eventId}`. Two listeners per period: the events ending
 * on or after its start (one range on `to`, a single-field index) and the yearly ones (their
 * repeats are worked out in LocalEvent); events starting after the period are dropped here. A
 * refused or failed listener is an empty list: the events are an extra, never an error.
 */
class LocalEventRepositoryImpl(private val firestore: AppFirestore) : LocalEventRepository {

    private fun events(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection(COLLECTION)

    private fun QuerySnapshot.toEvents(workplaceId: String): List<LocalEvent> =
        documents.mapNotNull { doc -> runCatching { doc.data<LocalEventDto>().toDomain(doc.id, workplaceId) }.getOrNull() }

    private fun Flow<QuerySnapshot>.events(workplaceId: String): Flow<List<LocalEvent>> =
        map { it.toEvents(workplaceId) }
            .catch { e ->
                ListenerErrors.report(e)
                emit(emptyList())
            }
            // Nothing yet from one listener must not hold the other back.
            .onStart { emit(emptyList()) }

    override fun observeEvents(workplaceId: String, from: LocalDate, to: LocalDate): Flow<List<LocalEvent>> {
        val current = events(workplaceId).where { "to" greaterThanOrEqualTo from.toString() }.snapshots.events(workplaceId)
        val yearly = events(workplaceId).where { "yearly" equalTo true }.snapshots.events(workplaceId)
        return combine(current, yearly) { a, b ->
            (a + b).distinctBy { it.id }.filter { it.yearly || it.from <= to }
        }.distinctUntilChanged()
    }

    override suspend fun saveEvent(event: LocalEvent, workplaceIds: List<String>): Result<Unit> = runCatching {
        val batch = firestore.batch()
        workplaceIds.forEach { workplaceId ->
            batch.set(events(workplaceId).document(event.id), LocalEventDto.of(event, workplaceId))
        }
        batch.commit()
    }

    override suspend fun deleteEvent(workplaceId: String, eventId: String): Result<Unit> = runCatching {
        events(workplaceId).document(eventId).delete()
    }

    override fun newId(): String = firestore.collection(COLLECTION).document.id

    companion object {
        const val COLLECTION = "local_events"
    }
}
